/**
 * AttendanceDetailDialog — 2026-02-26
 *
 * User ask: "under that employees report card they can see their
 * attendance and or occurrences with a clickable popout that displays
 * more - date and occurrence".
 *
 * Read-only history dialog showing every attendance event in the 6-mo
 * window with date, type chip, notes, excused flag, and who logged it.
 * Opened from the "See all occurrences" link on the Report Card
 * attendance section.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogTitle,
} from "./ui/dialog";
import {
  Clock, Prohibit, DoorOpen, WarningCircle, ShieldCheck, X, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  absence:          { label: "Call-off",          Icon: Prohibit,       cls: "bg-rose-100 text-rose-800" },
  tardy:            { label: "Tardy",             Icon: Clock,          cls: "bg-amber-100 text-amber-800" },
  left_early:       { label: "Left Early",        Icon: DoorOpen,       cls: "bg-orange-100 text-orange-800" },
  ncns:             { label: "No-Call/No-Show",   Icon: WarningCircle,  cls: "bg-slate-900 text-white" },
  fmla:             { label: "FMLA",              Icon: ShieldCheck,    cls: "bg-sky-100 text-sky-800" },
  bereavement:      { label: "Bereavement",       Icon: ShieldCheck,    cls: "bg-sky-100 text-sky-800" },
  protected_leave:  { label: "Protected Leave",   Icon: ShieldCheck,    cls: "bg-sky-100 text-sky-800" },
  other_excused:    { label: "Excused",           Icon: ShieldCheck,    cls: "bg-sky-100 text-sky-800" },
};

const fmtDate = (ymd) => {
  if (!ymd) return "";
  try {
    // Force local interpretation to avoid off-by-one UTC drift.
    const [y, m, d] = ymd.split("-").map((n) => Number(n));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return ymd; }
};

// Rolling 6-month expiration for the occurrence counter. An event
// counts against a crew member for 6 months from its date; after that
// it drops off the ledger. This helper returns both the expiration date
// (YYYY-MM-DD) and a human-readable "in X days / expired" hint so the
// manager can see at a glance how much longer the write-up sticks.
// (User ask 2026-02-27 — "show the rolling 6 on occurrence to display
// when it will expire".)
const rollingExpiry = (ymd) => {
  if (!ymd) return { date: "", label: "" };
  try {
    const [y, m, d] = ymd.split("-").map((n) => Number(n));
    const start = new Date(y, (m || 1) - 1, d || 1);
    const exp = new Date(start.getFullYear(), start.getMonth() + 6, start.getDate());
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((exp.getTime() - today.getTime()) / 86400000);
    const dateStr = exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    let label;
    if (days < 0) label = "Expired";
    else if (days === 0) label = "Expires today";
    else if (days === 1) label = "Expires tomorrow";
    else if (days <= 14) label = `Expires in ${days} days`;
    else if (days <= 60) label = `Expires ${dateStr} · ${days} days`;
    else label = `Expires ${dateStr}`;
    return { date: dateStr, label, daysLeft: days };
  } catch { return { date: "", label: "" }; }
};

export default function AttendanceDetailDialog({ open, onOpenChange, crewId, crewName, actorId, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !crewId || !actorId) {
      if (!open) { setData(null); setError(""); }
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/crew/${crewId}/attendance`, { params: { actor_id: actorId } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load attendance history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, crewId, actorId]);

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event? This is soft-delete and audit-logged.")) return;
    try {
      await axios.delete(`${API}/attendance/events/${id}`, { params: { actor_id: actorId } });
      setData((d) => d ? { ...d, events: (d.events || []).filter((e) => e.id !== id) } : d);
      toast.success("Event deleted");
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete event");
    }
  };

  const snap = data?.snapshot;
  const events = data?.events || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-lg w-[calc(100%-16px)] max-h-[88vh] overflow-hidden rounded-3xl bg-white"
        data-testid="attendance-detail-dialog"
      >
        <DialogTitle className="sr-only">Attendance history for {crewName || "crew"}</DialogTitle>

        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Attendance · rolling 6mo</p>
              <p className="text-xl font-black text-slate-900 mt-0.5 truncate">{crewName || "Crew"}</p>
              {snap && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <ThresholdChip snapshot={snap} />
                  {snap.next_threshold && (
                    <span className="text-[11px] text-slate-500">
                      {snap.next_threshold.occ - snap.occurrences} away from {snap.next_threshold.label}
                    </span>
                  )}
                </div>
              )}
              {/* Next-to-drop hint. The soonest countable event's
                  expiry tells the manager when the occurrence total
                  will tick down by one. Skipped when there are no
                  countable events. (User ask 2026-02-27.) */}
              {(() => {
                const countable = events.filter((e) => !e.excused);
                if (!countable.length) return null;
                const soonest = [...countable].sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];
                const exp = rollingExpiry(soonest.date);
                if (!exp.label || exp.daysLeft < 0) return null;
                return (
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="attendance-next-to-drop">
                    Next occurrence drops <span className="font-black text-slate-800">{exp.date}</span> ({exp.daysLeft} days)
                  </p>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
              aria-label="Close"
              data-testid="attendance-detail-close"
            >
              <X weight="bold" className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 max-h-[70vh]">
          {loading && (
            <div className="p-10 text-center text-sm text-slate-500">Loading history…</div>
          )}
          {!loading && error && (
            <div className="p-6">
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">{error}</div>
            </div>
          )}
          {!loading && !error && events.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-sm font-bold text-slate-700">Clean sheet — no attendance events in the last 6 months.</p>
              <p className="text-[11px] text-slate-500 mt-1">Occurrences roll off automatically after 6 months.</p>
            </div>
          )}
          {!loading && !error && events.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {events.map((e) => {
                const meta = TYPE_META[e.type] || { label: e.type, Icon: Clock, cls: "bg-slate-100 text-slate-600" };
                return (
                  <li
                    key={e.id}
                    className="px-5 py-3"
                    data-testid={`attendance-event-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.cls}`}>
                            <meta.Icon weight="fill" className="w-3 h-3" />
                            {meta.label}
                          </span>
                          {e.excused && (
                            <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                              Excused
                            </span>
                          )}
                          <span className="text-[13px] font-black text-slate-900">{fmtDate(e.date)}</span>
                          {e.minutes_late != null && e.minutes_late > 0 && (
                            <span className="text-[11px] text-slate-500">· {e.minutes_late} min late</span>
                          )}
                          {/* Rolling 6-month expiration pill. Hidden for
                              excused events because they never counted
                              in the first place. Amber under 14 days,
                              slate otherwise, rose once already expired
                              (should be filtered by backend but shown
                              defensively). */}
                          {!e.excused && (() => {
                            const exp = rollingExpiry(e.date);
                            if (!exp.label) return null;
                            const tone = exp.daysLeft < 0
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : exp.daysLeft <= 14
                                ? "bg-amber-50 text-amber-800 border-amber-200"
                                : "bg-slate-50 text-slate-600 border-slate-200";
                            return (
                              <span
                                className={`text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded border ${tone}`}
                                title={`Counts against occurrence total for 6 months from ${fmtDate(e.date)}`}
                                data-testid={`attendance-event-${e.id}-expiry`}
                              >
                                {exp.label}
                              </span>
                            );
                          })()}
                        </div>
                        {e.notes && (
                          <p className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap break-words">
                            {e.notes}
                          </p>
                        )}
                        {e.excused && e.excused_reason && (
                          <p className="text-[11px] text-sky-800 mt-1 italic">
                            Excused: {e.excused_reason}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1 font-semibold">
                          Logged by {e.logged_by_name || "Manager"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeEvent(e.id)}
                        className="text-slate-400 hover:text-rose-500 p-1 flex-shrink-0"
                        aria-label="Delete event"
                        data-testid={`attendance-event-${e.id}-delete`}
                      >
                        <Trash weight="bold" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Small chip that summarizes the current threshold level. Also used
// inline in the report card, so exporting it.
export function ThresholdChip({ snapshot }) {
  if (!snapshot) return null;
  const level = snapshot.level || "clear";
  const cls = ({
    clear:       "bg-emerald-100 text-emerald-800 border-emerald-200",
    coaching:    "bg-amber-100  text-amber-900   border-amber-200",
    written:     "bg-orange-100 text-orange-900  border-orange-200",
    final:       "bg-rose-100   text-rose-900    border-rose-200",
    termination: "bg-slate-900  text-white       border-slate-900",
  }[level]);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full border ${cls}`} data-testid="attendance-threshold-chip">
      {level === "clear" ? "Clear" : snapshot.label} · {snapshot.occurrences}
      {snapshot.ncns > 0 && ` · ${snapshot.ncns} NCNS`}
    </span>
  );
}
